import React, { useState } from 'react';
import { AlertTriangle, Shield, Phone, Users, Clock, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CrisisDisclaimerProps {
  isOpen: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

export const CrisisDisclaimer: React.FC<CrisisDisclaimerProps> = ({ 
  isOpen,
  onAccept,
  onCancel
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DisclaimerContent onAccept={onAccept} onCancel={onCancel} />
      </DialogContent>
    </Dialog>
  );
};

const DisclaimerContent: React.FC<{ onAccept: () => void; onCancel: () => void }> = ({ onAccept, onCancel }) => {
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-primary">
          <AlertTriangle className="h-5 w-5" />
          CRITICAL SAFETY NOTICE - Crisis & Safety Suite
        </DialogTitle>
      </DialogHeader>

      <Alert className="border-primary/30 bg-primary/5">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-sm font-bold">
          EMERGENCY SITUATIONS: If you are in immediate danger or having thoughts of suicide or self-harm, DO NOT rely on this app. Use the crisis resources provided or call emergency services immediately.
        </AlertDescription>
      </Alert>

      <div className="space-y-4 text-sm text-muted-foreground">
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Not Professional Crisis Intervention</p>
            <p>This Crisis & Safety Suite provides <strong>self-help tools and resources only</strong>. It is NOT professional crisis intervention, therapy, or medical treatment. These tools cannot replace trained crisis counselors, therapists, or emergency responders.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Phone className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">When to Seek Immediate Help</p>
            <p>Contact emergency services or crisis hotlines immediately if you experience:</p>
            <ul className="mt-2 ml-4 space-y-1 list-disc">
              <li>Active suicidal thoughts or plans</li>
              <li>Thoughts of harming yourself or others</li>
              <li>Severe mental health crisis or breakdown</li>
              <li>Psychotic episodes or loss of reality</li>
              <li>Substance abuse emergencies</li>
              <li>Any situation requiring immediate intervention</li>
            </ul>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Heart className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Intended Use</p>
            <p>These tools are designed for:</p>
            <ul className="mt-2 ml-4 space-y-1 list-disc">
              <li>Building coping strategies between professional sessions</li>
              <li>Creating safety plans with professional guidance</li>
              <li>Managing ongoing mental health conditions (not acute crises)</li>
              <li>Supplementing professional care (never replacing it)</li>
            </ul>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Response Limitations</p>
            <p>This app cannot provide <strong>real-time crisis response</strong>. There may be delays or technical issues. Never rely on this app during active emergencies or when immediate help is needed.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Age & Supervision Requirements</p>
            <p>You must be <strong>18+ years old</strong> to use crisis tools independently. Users under 18 require supervision by a parent, guardian, or mental health professional. Minors experiencing crisis should contact emergency services or trusted adults immediately.</p>
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg border border-primary/20">
          <p className="font-medium text-foreground mb-2">Your Safety Responsibility</p>
          <p>By using these tools, you acknowledge that:</p>
          <ul className="mt-2 space-y-1 list-disc ml-4">
            <li>You understand the limitations of self-help tools</li>
            <li>You will seek professional help for serious mental health concerns</li>
            <li>You will contact emergency services for any immediate danger</li>
            <li>You are responsible for your own safety and wellbeing</li>
            <li>These tools supplement but never replace professional care</li>
          </ul>
        </div>

        <div className="text-xs text-muted-foreground border-t pt-4">
          <p className="mb-2"><strong>Data Security:</strong> All personal information is encrypted and confidential, but this app is not monitored by crisis professionals.</p>
          <p className="mb-2"><strong>Consent Recording:</strong> Your acceptance of this disclaimer will be securely recorded with a timestamp for legal protection and safety purposes.</p>
          <p className="mb-2"><strong>Liability:</strong> This app and its creators cannot be held responsible for any outcomes from using these crisis support tools. Professional supervision is strongly recommended.</p>
          <p><strong>Questions?</strong> Contact us at <a href="mailto:support@therappi.app" className="text-primary hover:underline">support@therappi.app</a></p>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button onClick={onCancel} variant="outline" className="flex-1">
          I Do Not Accept
        </Button>
        <Button onClick={onAccept} className="flex-1 bg-primary hover:bg-primary/90">
          I Understand and Accept Responsibility
        </Button>
      </div>
    </div>
  );
};